import type { AxiosError } from 'axios'
import { useIntl } from 'react-intl'

import { getBuyingFormatSequence } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedAuction } from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage, showSnackbar } from '@b-stock/fe-core/bstock-react'

import { isAxiosError } from '@/buyer-portal/components/ErrorBanner'

type AxiosErrorType = {
  errorMessage: string
}

const ns = 'BuyNow.BuyNowActions.errors'

const getBuyNowAxiosErrorMessageId = (
  error: AxiosError<AxiosErrorType>,
  buyNowOnly: boolean
) => {
  const status = error.response?.status
  const message = error.response?.data.errorMessage ?? ''
  if (status === 400) {
    if (message === 'no shipment quote selected') {
      return `${ns}.400.noQuoteSelected`
    } else {
      return `${ns}.400.other`
    }
  } else if (status === 403) {
    switch (message) {
      case 'auction schedule not set':
        return `${ns}.403.scheduleNotSet`
      case 'auction was canceled':
        return `${ns}.403.wasCanceled`
      case 'auction ended':
        return `${ns}.403.${buyNowOnly ? 'endedBuyNowOnly' : 'ended'}`
      case 'auction does not allow buy now':
        return `${ns}.403.buyNowNotAllowed`
      case 'buy now window is not started yet':
        return `${ns}.403.buyNowNotStarted`
      case 'buy now window ended':
        return `${ns}.403.buyNowEnded`
      case 'forbidden action for user':
        return `${ns}.403.forbiddenAction`
      case 'listing not published':
        return `${ns}.403.notPublished`
      default:
        return undefined
    }
  } else if (status === 401 || status === 404) {
    return `${ns}.${status}`
  }
  return undefined
}

export const useOnBuyNowError = (auction: OrchestratedAuction) => {
  const { formatMessage } = useIntl()

  const onBuyNowError = (error: unknown) => {
    if (isAxiosError(error)) {
      const messageId = getBuyNowAxiosErrorMessageId(
        error as AxiosError<AxiosErrorType>,
        getBuyingFormatSequence(auction) === 'BINO'
      )
      let message: string

      if (messageId) {
        message = formatMessage({ id: messageId })
      } else {
        message = formatMessage(
          { id: `${ns}.400.other` },
          { message: JSON.stringify(error.response?.data.errorMessage) }
        )
      }

      if (message) {
        showSnackbar({
          content: message,
          options: { type: 'error' },
        })
      }
      return
    }

    showSnackbar({
      content: <FormattedMessage id="Common.error.somethingWentWrong" />,
      options: { type: 'error' },
    })
  }

  return onBuyNowError
}
